import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import PropTypes from 'prop-types';
import { formatTime } from '../../utils';
let translations = {
  ARIA_TIMEBAR_LABEL: 'Timebar',
  ARIA_VIDEO_LABEL: 'Video Player',
  ARIA_VOLUME: 'Current Volume Level',
  ARIA_TIMEBAR_CURRENT_TIME: ({
    currentTime,
    duration
  }) => `${formatTime(currentTime)} / ${formatTime(duration)}`,
  FILE_PLACEHOLDER: 'Untitled',
  FINISH: 'Finish',
  WEBCAM_VIDEO_SELECTION_LABEL: 'Select video source',
  WEBCAM_AUDIO_SELECTION_LABEL: 'Select audio source',
  PLAYBACK_PAUSE: 'Pause',
  PLAYBACK_PLAY: 'Play',
  SAVE_MEDIA: 'Save Media',
  SR_FILE_INPUT: 'Title',
  START: 'Start Recording',
  START_OVER: 'Start Over',
  DEFAULT_ERROR: 'Something went wrong. Please try again.',
  ERROR_SCREEN_SHARING_SYSTEM_PERMISSION: 'Unable to share your screen. Please review your system permissions and try again.',
  WEBCAM_DISABLED: 'Webcam Disabled',
  MICROPHONE_DISABLED: 'Microphone Disabled',
  SCREEN_SHARING_CONTROL: 'Screen Capture',
  SCREEN_SHARING_CONTROL_LABEL_ACTIVATE: 'Screen sharing enabled',
  SCREEN_SHARING_CONTROL_LABEL_DEACTIVATE: 'Screen sharing disabled',
  SCREEN_SHARING_CONTROL_ACTIVATE: 'Enabled',
  SCREEN_SHARING_CONTROL_DEACTIVATE: 'Disabled',
  CAMERA: 'Camera',
  MICROPHONE: 'Microphone',
  NO_INPUT_STREAM_AVAILABLE: 'To start recording, share your screen, or authorize camera or microphone access.',
  PAUSE: 'Pause',
  CONTINUE: 'Continue',
  PAUSED_VIDEO: 'Recording is paused',
  PICTURE_IN_PICTURE: 'Picture-in-Picture',
  PICTURE_IN_PICTURE_TOOLTIP: 'To enable your webcam, share your entire screen',
  PICTURE_IN_PICTURE_MODAL_HEADING: 'Enable camera',
  PICTURE_IN_PICTURE_MODAL_BODY: 'Would you like to enable your camera during full screen recording?',
  PICTURE_IN_PICTURE_MODAL_CANCEL: 'No',
  PICTURE_IN_PICTURE_MODAL_CONFIRM: 'Yes',
  PICTURE_IN_PICTURE_MODAL_CLOSE: 'Close',
  CAMERA_EFFECTS: 'Camera Effects',
  BACKGROUND_BLUR: 'Background Blur',
  MIRROR_MY_VIDEO: 'Mirror My Video',
  NO_SYSTEM_PERMISSION_OVERLAY_HEADING: 'Give your browser permission to record',
  NO_SYSTEM_PERMISSION_OVERLAY_TEXT: 'Grant your browser permission to record by allowing access to your camera and microphone.',
  NO_SYSTEM_PERMISSION_OVERLAY_LINK: 'Learn more ...',
  NO_SYSTEM_VIDEO_PERMISSION_TOOLTIP: "Please check your system's camera settings.",
  NO_SYSTEM_AUDIO_PERMISSION_TOOLTIP: "Please check your system's microphone settings",
  SELECTED_VALUE: 'Selected value:'
};
export const TranslationPropTypes = Object.keys(translations).reduce((pt, key) => _objectSpread(_objectSpread({}, pt), {}, {
  [key]: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
}), {});
export function applyTranslations(consumerTranslations) {
  translations = _objectSpread(_objectSpread({}, translations), consumerTranslations);
}
export function translate(key) {
  return translations[key];
}
export function translateWithFn(key, args) {
  return translations[key](args);
}